import type { IFlightMonitorRow } from '@/lib/flights/types';

// The router state every door hands to the declare flow on `/express` (unified-declare,
// brief §1). Router state, never query params — the same rule Express follows
// (`express.tsx`): it does not survive a refresh, by design, and nothing about a flight
// or a passenger leaks into the URL.
//
//   { declare: { source: 'BOARD' } }            step 1, the departures board (default)
//   { declare: { source: 'FILE' } }             step 1, the file source (the drop zone)
//   { declare: { source: 'FILE', importUrn } }  step 1, the file source on that import
//                                               (`/imports` hands a ready import over)
//   { declare: { source: 'PNRS' } }             (withdrawn 2026-11-04) read as the board
//   { declare: { flightUrns: [...] } }          straight to step 2 with these flights
//   { candidate: IFlightMonitorRow }            legacy single-flight hand-over (passenger
//                                               search) — still honoured, same as one URN

export type DeclareSource = 'BOARD' | 'FILE';

export interface IDeclareRouteState {
  declare?: {
    source?: DeclareSource;
    /** Preselected flights: the flow skips step 1. */
    flightUrns?: string[];
    /** The file source's import — step 1 opens the «Archivo» tab on it (implies `FILE`). */
    importUrn?: string;
  };
  /** Legacy hand-over from the passenger search (one flight row). */
  candidate?: IFlightMonitorRow;
}

/** Open step 1 of the declare flow on a given source. */
export function declareSourceState(source: DeclareSource): IDeclareRouteState {
  return { declare: { source } };
}

/** Open step 2 of the declare flow with these flights already chosen. */
export function declareFlightsState(flightUrns: readonly string[]): IDeclareRouteState {
  return { declare: { flightUrns: [...flightUrns] } };
}

/** Open the file source of step 1 on an uploaded import (`/imports` → «Declarar»). */
export function declareImportState(importUrn: string): IDeclareRouteState {
  return { declare: { source: 'FILE', importUrn } };
}

/** Read whatever a door handed over; unknown shapes read as "start at step 1". */
export function readDeclareRouteState(state: unknown): IDeclareRouteState {
  if (!state || typeof state !== 'object') return {};
  const s = state as IDeclareRouteState;
  const out: IDeclareRouteState = {};
  if (s.candidate && typeof s.candidate === 'object') out.candidate = s.candidate;
  const d = s.declare;
  if (d && typeof d === 'object') {
    // «PNRs sueltos» is withdrawn for now: a door still asking for it gets the board.
    let source: DeclareSource | undefined =
      d.source === 'BOARD' || d.source === 'FILE' ? d.source : (d.source as unknown) === 'PNRS' ? 'BOARD' : undefined;
    const flightUrns = Array.isArray(d.flightUrns)
      ? d.flightUrns.filter((u): u is string => typeof u === 'string' && u.length > 0)
      : undefined;
    // An import only means something to the file source: it implies FILE, and a
    // contradicting source drops it rather than guessing.
    const importUrn =
      typeof d.importUrn === 'string' && d.importUrn.trim() && (!source || source === 'FILE')
        ? d.importUrn.trim()
        : undefined;
    if (importUrn) source = 'FILE';
    out.declare = {
      ...(source ? { source } : {}),
      ...(flightUrns && flightUrns.length ? { flightUrns } : {}),
      ...(importUrn ? { importUrn } : {}),
    };
  }
  return out;
}
